import { signInWithCustomToken } from 'firebase/auth';
import { canReuseExistingSession, decodeCustomTokenPayload } from './ssoReuse.js';
export { canReuseExistingSession, decodeCustomTokenPayload, } from './ssoReuse.js';
/**
 * T-08 SSO handover: tenant-portal / saas-admin の機能カードから `#sso_token=<JWT>`
 * 付きで遷移してきた場合、url fragment の token を取り出して `signInWithCustomToken`
 * で sign-in し、URL から fragment を除去する。
 *
 * 各 app の起動時 (main.tsx) で render より前に await することで、AuthProvider が
 * onAuthStateChanged を最初に評価する時点で signed-in 状態を観測できるようにする。
 *
 * 本 package は Firebase Web SDK (`firebase/auth`) に依存するため `@kawai/shared`
 * には置けない（shared は Functions からも参照されるため Web SDK 依存を持てない）。
 */
/**
 * Firebase Custom Token は base64url JWT（3 セグメント / `header.payload.signature`）。
 * 受信値が JWT 形状から逸脱していた場合は早期 reject する（log noise 削減 +
 * `signInWithCustomToken` への不要なリクエスト抑制）。
 *
 * - 各セグメントは base64url 文字 (`A-Za-z0-9_-`) のみ
 * - 末尾の `=` padding は base64url JWT では除去されるため許容しない
 */
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
/** Firebase Custom Token の妥当な長さ範囲（短すぎ / DoS 緩和の両端）。 */
const MIN_TOKEN_LENGTH = 20;
const MAX_TOKEN_LENGTH = 4096;
/**
 * URL fragment 文字列から `sso_token` を取り出す純関数。
 *
 * - 入力例: `'#sso_token=abc.def.ghi'` / `'#foo=1&sso_token=abc.def.ghi'`
 * - 値が無い / 長さ範囲外 / JWT 3 セグメント形状でない場合は `null`
 */
export function parseSsoTokenFromHash(hash) {
    if (!hash || hash.length <= 1)
        return null;
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    const params = new URLSearchParams(raw);
    const token = params.get('sso_token');
    if (!token)
        return null;
    if (token.length < MIN_TOKEN_LENGTH || token.length > MAX_TOKEN_LENGTH)
        return null;
    if (!JWT_SHAPE.test(token))
        return null;
    return token;
}
/**
 * 副作用: window.history.replaceState で URL から fragment を除去する。
 * test 環境で window が無い場合は何もしない（happy-dom では存在）。
 */
export function stripHashFromCurrentUrl() {
    if (typeof window === 'undefined')
        return;
    const url = new URL(window.location.href);
    if (!url.hash)
        return;
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
}
/**
 * Hash から SSO token を取り出して sign-in する。
 *
 * - token が無い → no-op で `{ consumed: false }`
 * - S-91: 既に同一人物・同一 claims の永続セッションがあれば `signInWithCustomToken` を
 *   省略（identitytoolkit 2 往復 = boot ローダー区間の主往復を削減）→
 *   `{ consumed: true, reusedSession: true }`
 * - sign-in 成功 → fragment 除去 → `{ consumed: true }`
 * - sign-in 失敗 → fragment 除去（漏洩防止）+ error log → `{ consumed: false, error }`
 *
 * 失敗してもアプリ起動は継続する（通常 AuthGuard が /signin に redirect する）。
 */
export async function consumeSsoTokenFromHash(auth, hash) {
    const token = parseSsoTokenFromHash(hash);
    if (!token)
        return { consumed: false };
    // S-91: 既存セッションの再利用判定。判定不能（decode 失敗等）は従来経路へ。
    try {
        const payload = decodeCustomTokenPayload(token);
        if (payload?.uid) {
            // IndexedDB からの永続セッション復元を待つ（ネットワーク往復なし・数十 ms）。
            await auth.authStateReady();
            const current = auth.currentUser;
            if (current && current.uid === payload.uid) {
                // getIdTokenResult はキャッシュ済み ID token のローカル decode（失効時のみ refresh 1 往復）。
                const idToken = await current.getIdTokenResult();
                if (canReuseExistingSession(payload.claims, idToken.claims)) {
                    stripHashFromCurrentUrl();
                    return { consumed: true, reusedSession: true };
                }
            }
        }
    }
    catch {
        // 再利用判定の失敗は無視して通常の sign-in にフォールバック
    }
    try {
        await signInWithCustomToken(auth, token);
        stripHashFromCurrentUrl();
        return { consumed: true };
    }
    catch (err) {
        // eslint-disable-next-line no-console
        console.error('[auth-bootstrap] signInWithCustomToken failed:', err);
        stripHashFromCurrentUrl();
        return { consumed: false, error: err };
    }
}
// ---------- S-75: 管理者 MFA（TOTP / 任意 opt-in）----------
// SDK glue + 純関数 + React UI（MfaChallenge / MfaSettings）を re-export。
export { attemptEmailPasswordSignIn, resolveTotpChallenge, startTotpEnrollment, completeTotpEnrollment, hasEnrolledTotp, getEnrolledTotpFactor, unenrollTotp, } from './mfa.js';
export { normalizeTotpCode, validateTotpCode, getAuthErrorCode, isMfaRequiredError, mapMfaErrorMessage, buildTotpDisplayName, } from './mfaPure.js';
export { MfaChallenge } from './MfaChallenge.js';
export { MfaSettings } from './MfaSettings.js';
// ---------- S-89: 共通サインインカード（管理系 5 app の SignInPage 本体）----------
export { SignInCard } from './SignInCard.js';
export { validateSignInForm, mapAuthErrorMessage, } from './signInPure.js';
//# sourceMappingURL=index.js.map